import { Layout } from "@/components/Layout";
import { SEO } from "@/components/SEO";
import { StructuredData } from "@/components/StructuredData";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Star } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect } from "react";
import testimonialsHero from "@/assets/testimonials-hero.jpg";

const testimonialSchema = z.object({
  name: z.string().trim().min(2, "Name must be at least 2 characters").max(100, "Name must be less than 100 characters"),
  role: z.string().trim().min(2, "Role must be at least 2 characters").max(100, "Role must be less than 100 characters"),
  content: z.string().trim().min(10, "Review must be at least 10 characters").max(500, "Review must be less than 500 characters"),
  rating: z.number().min(1).max(5),
});

type TestimonialFormData = z.infer<typeof testimonialSchema>;

interface Testimonial {
  id?: string;
  name: string;
  role: string;
  content: string;
  rating: number;
  avatar: string;
}

const Testimonials = () => {
  const { toast } = useToast();
  const [selectedRating, setSelectedRating] = useState(5);
  const [liveTestimonials, setLiveTestimonials] = useState<Testimonial[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TestimonialFormData>({
    resolver: zodResolver(testimonialSchema),
    defaultValues: {
      rating: 5,
    },
  });

  // Fetch approved testimonials
  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    const { data, error } = await supabase
      .from("testimonials")
      .select("*")
      .eq("approved", true)
      .order("created_at", { ascending: false });

    if (!error && data) {
      const formattedTestimonials = data.map((t) => ({
        id: t.id,
        name: t.name,
        role: t.role,
        content: t.content,
        rating: t.rating,
        avatar: t.name
          .split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
          .slice(0, 2),
      }));
      setLiveTestimonials(formattedTestimonials);
    }
  };

  const onSubmit = async (data: TestimonialFormData) => {
    setIsSubmitting(true);
    try {
      const { error } = await supabase.from("testimonials").insert([
        {
          name: data.name,
          role: data.role,
          content: data.content,
          rating: selectedRating,
          approved: false, // Will be reviewed before showing
        },
      ]);

      if (error) throw error;

      toast({
        title: "Thank you for your review!",
        description: "Your testimonial will be published after review.",
      });

      reset();
      setSelectedRating(5);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit testimonial. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const staticTestimonials: Testimonial[] = [
    {
      name: "Sarah Johnson",
      role: "Game Developer",
      content:
        "This is my go-to tool for generating random numbers in game development. The cryptographic security gives me confidence that the randomness is truly fair.",
      rating: 5,
      avatar: "SJ",
    },
    {
      name: "Michael Chen",
      role: "Teacher",
      content:
        "I use the random picker daily for classroom activities. It's simple, fast, and the students love seeing their names drawn randomly!",
      rating: 5,
      avatar: "MC",
    },
    {
      name: "Emily Rodriguez",
      role: "Event Organizer",
      content:
        "The raffle drawing feature has been invaluable for our charity events. Completely transparent and trustworthy random selection.",
      rating: 5,
      avatar: "ER",
    },
    {
      name: "David Park",
      role: "Security Engineer",
      content:
        "Finally, a random number generator that uses proper cryptographic functions. Perfect for generating secure passwords and tokens.",
      rating: 5,
      avatar: "DP",
    },
    {
      name: "Lisa Thompson",
      role: "Lottery Enthusiast",
      content:
        "I've been using this for my lottery numbers for months. The interface is clean and the numbers are truly random. Love it!",
      rating: 5,
      avatar: "LT",
    },
    {
      name: "James Wilson",
      role: "Tabletop Gamer",
      content:
        "The dice roller is fantastic! I use it for all my online D&D sessions. Much better than physical dice that can be biased.",
      rating: 5,
      avatar: "JW",
    },
  ];

  const allTestimonials = [...liveTestimonials, ...staticTestimonials];

  // Calculate average rating for schema
  const totalRating = allTestimonials.reduce((sum, t) => sum + t.rating, 0);
  const averageRating = allTestimonials.length > 0 
    ? (totalRating / allTestimonials.length).toFixed(1) 
    : "5.0";

  return (
    <Layout>
      <SEO 
        title="User Reviews & Testimonials - Random Number Generator"
        description="Read what users say about our cryptographically secure random number generator. Join thousands of satisfied users for fair lottery draws, games, and more."
        canonical="https://numbers-generator.com/testimonials"
        keywords="random generator reviews, user testimonials, customer feedback, trusted RNG"
      />
      <StructuredData 
        type="Review"
        review={{
          itemName: "RandomGen - Random Number Generator",
          ratingValue: parseFloat(averageRating),
          reviewCount: allTestimonials.length,
          bestRating: 5
        }}
      />
      <div className="container mx-auto px-4 py-12">
        <header className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
            RandomGen Numbers Generator Testimonials & Reviews
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            See what our users are saying about RandomGen
          </p>
        </header>

        {/* Testimonial Submission Form */}
        <Card className="p-8 max-w-2xl mx-auto mb-12">
          <h2 className="text-2xl font-bold mb-6 text-center">Share Your Experience</h2>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <Label htmlFor="name">Your Name</Label>
              <Input
                id="name"
                {...register("name")}
                placeholder="John Doe"
                className="mt-1.5"
              />
              {errors.name && (
                <p className="text-sm text-destructive mt-1">{errors.name.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="role">Your Role/Occupation</Label>
              <Input
                id="role"
                {...register("role")}
                placeholder="Software Developer"
                className="mt-1.5"
              />
              {errors.role && (
                <p className="text-sm text-destructive mt-1">{errors.role.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="rating">Rating</Label>
              <div className="flex gap-2 mt-1.5">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setSelectedRating(star)}
                    className="focus:outline-none transition-transform hover:scale-110"
                  >
                    <Star
                      className={`w-8 h-8 ${
                        star <= selectedRating
                          ? "fill-accent text-accent"
                          : "text-muted-foreground"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="content">Your Review</Label>
              <Textarea
                id="content"
                {...register("content")}
                placeholder="Tell us about your experience with RandomGen..."
                className="mt-1.5 min-h-[120px]"
              />
              {errors.content && (
                <p className="text-sm text-destructive mt-1">{errors.content.message}</p>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Submit Review"}
            </Button>
            <p className="text-sm text-muted-foreground text-center">
              Your review will be published after moderation
            </p>
          </form>
        </Card>

        {/* Display Testimonials */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {allTestimonials.map((testimonial, index) => (
            <Card key={index} className="p-6 hover:shadow-[var(--shadow-medium)] transition-[var(--transition-smooth)]">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-bold">
                  {testimonial.avatar}
                </div>
                <div>
                  <p className="font-bold">{testimonial.name}</p>
                  <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                </div>
              </div>

              <div className="flex gap-1 mb-3">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-accent text-accent" />
                ))}
              </div>

              <p className="text-muted-foreground">{testimonial.content}</p>
            </Card>
          ))}
        </div>

        <div className="mt-12 text-center">
          <Card className="p-8 max-w-2xl mx-auto bg-gradient-to-r from-primary/5 to-accent/5">
            <h2 className="text-2xl font-bold mb-4">Join Thousands of Happy Users</h2>
            <p className="text-muted-foreground mb-6">
              Start using RandomGen today and experience truly random number generation
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <div className="text-center">
                <p className="text-3xl font-bold text-primary">50K+</p>
                <p className="text-sm text-muted-foreground">Active Users</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-accent">1M+</p>
                <p className="text-sm text-muted-foreground">Generations Daily</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-primary">99.9%</p>
                <p className="text-sm text-muted-foreground">Satisfaction Rate</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Hero Image at Bottom */}
        <div className="mt-12 rounded-2xl overflow-hidden shadow-2xl max-w-5xl mx-auto">
          <img 
            src={testimonialsHero} 
            alt="Customer testimonials and five-star reviews showing positive user feedback and ratings for RandomGen random number generator"
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>
      </div>
    </Layout>
  );
};

export default Testimonials;
